/**
 * Aircraft on a staff project map (MB, 27 Oct 2026; decisions 825 to 828; decision 765: staff only, so never a client
 * or Preview as client, whose map keeps the client portal's own rules): the organisation's own tracked aircraft as
 * on the Operations Hub (decision 827: every device of its tracking accounts, the trails of the last 12 hours, take-off,
 * flight time and landing, SOS and lost tracking in words), and the public air traffic inside the project's own
 * circle (its block boundaries plus 100 nm, decision 767), one icon per aircraft with Teslyn Field's rules
 * (`hubTracking`: a tracked aircraft also on a public feed shows once, from its tracker; public trails the last 2
 * hours while moving, nothing older than 10 minutes), credited.
 */
import { useEffect, useMemo, useState } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  DEFAULT_TRACKING_SETTINGS,
  cloudTrafficStatusText,
  hubTracking,
  publicRowsNear,
  trafficCircle,
  type HubTracking,
  type TrafficBlock,
} from '@teslyn/domain';
import { usePublicTraffic } from '../map/publicTraffic.ts';
import { loadHubTracking, type HubPublicTraffic, type HubTrackingRows } from '../ops/hubApi.ts';
import type { RemainingMapTracking } from './RemainingMap.tsx';

/** The organisation's own aircraft: the trail of the last 12 hours (the Hub's and Teslyn Field's default). */
export const PROJECT_MAP_TRACKER_HOURS = 12;
/** The tracker poller stores positions about once a minute (decision 419): read them each minute, as the Hub does. */
const TRACKER_REFRESH_MS = 60_000;

export interface ProjectAircraft {
  /** The aircraft for the map; null while none (or not staff). */
  readonly tracking: RemainingMapTracking | null;
  /** "Public air traffic: …" for the line under the map; null for a client or before Teslyn Cloud has it. */
  readonly text: string | null;
  /**
   * Everything `hubTracking` worked out (the Tracking assets list, fleet groups, public count) for a map with the
   * Hub's list (the live map, docs/specs/cloud/live-tracking.md §3); null while the trackers are not read yet.
   */
  readonly hub: HubTracking | null;
  /** The public traffic as read (the switch, the poller's words, the rows), for the source strip; null before. */
  readonly traffic: HubPublicTraffic | null;
  /** Loading or a failure of the trackers in words; null once read (and always for a client). */
  readonly status: string | null;
}

/** What a map with the Hub's Tracking assets list chooses (the project map takes the defaults). */
export interface ProjectAircraftOptions {
  /** The history of the organisation's own trackers, 1 to 48 hours (12 by default). */
  readonly hours?: number;
  /** Fleet groups ticked; none shows every aircraft. */
  readonly shownGroups?: readonly string[];
}

/** The organisation's own tracked aircraft for a staff project map, read each minute; null for a client. */
function useProjectTrackers(
  sb: SupabaseClient,
  project: { readonly id: string; readonly tenantId: string },
  staff: boolean,
  hours: number,
): { readonly rows: HubTrackingRows | null; readonly error: string | null } {
  const [rows, setRows] = useState<HubTrackingRows | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  // another project: nothing of the last one stays on the map
  useEffect(() => setRows(null), [project.id]);
  useEffect(() => {
    if (!staff) {
      setRows(null);
      return undefined;
    }
    let live = true;
    loadHubTracking(sb, project.tenantId, hours, project.id)
      .then((r) => {
        if (!live) return;
        setRows(r);
        setError(null);
      })
      .catch((e: unknown) => {
        if (!live) return;
        setRows(null);
        setError(e instanceof Error ? e.message : String(e));
      });
    const t = setTimeout(() => setTick((n) => n + 1), TRACKER_REFRESH_MS);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [sb, project.id, project.tenantId, staff, hours, tick]);
  return { rows, error };
}

export function useProjectAircraft(
  sb: SupabaseClient,
  project: { readonly id: string; readonly tenantId: string; readonly epsg: number | null },
  blocks: readonly TrafficBlock[],
  staff: boolean,
  clock: () => number = Date.now,
  options: ProjectAircraftOptions = {},
): ProjectAircraft {
  const target = useMemo(
    () => (staff ? { kind: 'project' as const, projectId: project.id } : null),
    [staff, project.id],
  );
  const traffic = usePublicTraffic(sb, target, clock);
  const hours = options.hours ?? PROJECT_MAP_TRACKER_HOURS;
  const { rows: own, error } = useProjectTrackers(sb, project, staff, hours);
  const groups = options.shownGroups;
  return useMemo(() => {
    if (!staff) return { tracking: null, text: null, hub: null, traffic: null, status: null };
    const status = error ? `Could not load the trackers: ${error}` : own ? null : 'Loading the trackers…';
    const text = traffic?.status ? cloudTrafficStatusText(traffic.status, clock()) : null;
    const all = traffic?.rows ?? [];
    const circle = trafficCircle({ epsg: project.epsg, blocks, contract_areas: null });
    // without block boundaries, the public positions its own request found
    const publicRows = circle ? publicRowsNear(all, circle) : all.filter((r) => r.project_id === project.id);
    if (!own && publicRows.length === 0) return { tracking: null, text, hub: null, traffic, status };
    const t = hubTracking({
      assets: own?.assets ?? [],
      rows: own?.rows ?? [],
      sos: own?.sos ?? new Set(),
      settings: own?.settings ?? DEFAULT_TRACKING_SETTINGS,
      nowMs: clock(),
      offsetH: -new Date().getTimezoneOffset() / 60,
      ...(groups ? { shownGroups: groups } : {}),
      publicRows,
    });
    return {
      tracking:
        t.aircraft.length > 0
          ? { aircraft: t.aircraft, trails: t.trails, colours: t.colours, credits: t.credits }
          : null,
      text,
      hub: own ? t : null,
      traffic,
      status,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the clock is read at each read of the aircraft
  }, [staff, traffic, own, error, project.id, project.epsg, blocks, groups]);
}
